import { AppContext } from '../../context';
import { Button, Radio } from '@goodtimeio/rain';
import { Duration } from 'luxon';
import { EventTemplate, Interview, InterviewOrigin, TimeRange } from '../../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { checkReasonExists, getActiveReasons, isOtherOptionSelected } from '../../utils/reschedule-reasons';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { mapAvailableTimeRangeToTimeRange, mapEventTemplateToTimeRange } from '../../utils/date-utils';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import ItineraryCalendarDisplay from '../Confirm/components/ItineraryCalendarDisplay';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Wrapper from '../../components/Wrapper';

import './styles.scss';

const isButtonDisabled = (rescheduleOpt: { reasonCode: string; note: string }) => {
    if (!rescheduleOpt.reasonCode) {
        return true;
    }
    if (isOtherOptionSelected(rescheduleOpt.reasonCode) && !rescheduleOpt.note) {
        return true;
    }
    return false;
};

const getDurationTimeOfMeeting = (
    interview: Interview,
    translate: (valueToTranslate: string, dynamicValueObject?: Record<string, unknown> | undefined) => string,
) => {
    const totalMin = interview.events.reduce(
        (acc: number, currentEvent: EventTemplate) => acc + currentEvent.duration,
        0,
    );
    const totalMill = totalMin * 60000;
    const duration = Duration.fromMillis(totalMill).shiftTo('hours', 'minutes');
    return duration.hours
        ? translate('confirm:hoursTotalTime', { count: duration.hours })
        : translate('confirm:minutesTotalTime', { count: duration.minutes });
};

const mapEventTimeRanges = (interview: Interview | undefined): TimeRange[] | undefined => {
    let eventsForTimeRanges: TimeRange[] = [];
    if (!interview) return eventsForTimeRanges;

    if (interview.status === 'confirmed') {
        eventsForTimeRanges = interview?.events.map(mapEventTemplateToTimeRange);
    } else {
        eventsForTimeRanges = interview?.settings?.availableTimeRanges.map(mapAvailableTimeRangeToTimeRange) || [];
    }

    return eventsForTimeRanges;
};

const Reschedule: React.FC = () => {
    const { hash } = useParams<{ hash: string }>();
    const { search } = useLocation();
    const { meetIntro, loadAvailabilitySearch, addTimeSlot, addDays, reschedule, translate, getMeetIntro } =
        useContext(AppContext);
    const history = useHistory();
    const [rescheduleOpt, setRescheduleOpt] = useState({ reasonCode: '', note: '' });
    const reasons = getActiveReasons(meetIntro?.data?.interview?.settings?.guestRescheduleReasons);
    const primaryColor = meetIntro?.data?.meetCompanyColor || '#6400E0';
    let textColor = '#FFFFFF';
    if (meetIntro?.data?.meetFontColor) {
        textColor = meetIntro?.data?.meetFontColor;
    }
    const logo = meetIntro?.data?.organization?.logoUrl;
    const backgroundImage = meetIntro?.data?.meetBackgroundUrlList[0];
    const dynamicButtonStyle = { background: primaryColor, border: primaryColor, color: textColor };

    const [hasResquetedAvailabilitySearch, setHasResquetedAvailabilitySearch] = useState(false);

    const redirectToConfirm = useCallback(() => {
        if (hash) {
            history.push(`/confirm/${hash}`);
        } else {
            history.push('/invalid-link');
        }
    }, [hash, history]);

    const redirectToIntro = useCallback(() => {
        if (hash) {
            history.push(`/intro/${hash}`);
        } else {
            history.push('/invalid-link');
        }
    }, [hash, history]);

    useEffect(() => {
        (async () => {
            const hasLoadedMeetIntro = Boolean(meetIntro?.data) && Boolean(!meetIntro?.isLoading);
            if (!meetIntro && !hasLoadedMeetIntro) {
                await getMeetIntro(hash);
            }
            if (hasLoadedMeetIntro) {
                if (!meetIntro) {
                    redirectToIntro();
                } else if (!meetIntro?.data?.canGuestReschedule && !meetIntro?.data?.interview?.settings?.needsReview) {
                    redirectToConfirm();
                }
            }
            if (!hasResquetedAvailabilitySearch) {
                loadAvailabilitySearch(hash);
                setHasResquetedAvailabilitySearch(true);
            }
        })();
    }, [
        hash,
        meetIntro,
        hasResquetedAvailabilitySearch,
        getMeetIntro,
        redirectToIntro,
        redirectToConfirm,
        loadAvailabilitySearch,
    ]);

    useEffect(() => {
        if (primaryColor) {
            document.documentElement.style.setProperty('--primary-date-picker-color', primaryColor);
        }
        if (textColor) {
            document.documentElement.style.setProperty('--primary-text-color', textColor);
        }
    }, [primaryColor, textColor]);

    const hasTheMeetingStarted = () => {
        const interviewStartTime = meetIntro?.data?.interview?.startTime;
        return interviewStartTime && new Date(interviewStartTime).getTime() - new Date().getTime() < 0;
    };

    const onReschedule = () => {
        if (hasTheMeetingStarted()) {
            return document.location.reload();
        }
        if (!isOtherOptionSelected(rescheduleOpt.reasonCode)) {
            setRescheduleOpt({
                reasonCode: rescheduleOpt.reasonCode,
                note: '',
            });
            reschedule(rescheduleOpt.reasonCode, '');
        } else {
            reschedule(rescheduleOpt.reasonCode, rescheduleOpt.note);
        }
        addTimeSlot('');
        addDays([]);
        return history.push(`/intro/${hash}`);
    };

    useEffect(() => {
        try {
            const searchParams = new URLSearchParams(search);
            const reason = searchParams.get('reason');
            const customReason = searchParams.get('customReason');

            if (reason) {
                if (checkReasonExists(reason)) {
                    setRescheduleOpt((_state) => ({
                        ..._state,
                        reasonCode: reason ?? '',
                        note: customReason ?? '',
                    }));
                }
            }
        } catch (err) {
            console.log('no reason');
        }
    }, [search]);

    const headerDescription = meetIntro?.data?.interview
        ? getDurationTimeOfMeeting(meetIntro.data.interview, translate)
        : null;
    const totalInterview = translate('general:interviews', { count: meetIntro?.data?.interview?.events?.length });

    const isDisabled = isButtonDisabled(rescheduleOpt);

    if (meetIntro?.data?.interview?.origin === InterviewOrigin.SUPERDAY) {
        document.location.href = `/s/intro/${hash}`;
        return null;
    }

    return (
        <Wrapper loading={meetIntro?.isLoading} backgroundImage={backgroundImage} logo={logo}>
            <div className="flexCardReschedule">
                <div className="flexCardBodyLeft">
                    <div className="flexCardBodyLeftHeader">
                        <span className="flexCardBodyLeftTitle">{translate('confirm:yourInterviewItinerary')}</span>
                        <span className="flexCardBodyLeftDescription">{`${totalInterview}, ${headerDescription}`}</span>
                    </div>
                    <ItineraryCalendarDisplay
                        timeRanges={mapEventTimeRanges(meetIntro?.data?.interview)}
                        primaryColor={primaryColor}
                        primaryTextColor={meetIntro?.data?.meetCompanyColor || '#6400E0'}
                    />

                    <div className="flexCardLowerBodyLeft">
                        <div className="recruiter">
                            <img src={meetIntro?.data?.emailSender?.profileUrl} alt=""></img>
                            <span className="flexCardBodyLeftInterviewerName">
                                {meetIntro?.data?.emailSender?.givenName}
                            </span>
                        </div>
                        <div className="reschedule-mail-recruiter-container">
                            <a href={`mailto:${meetIntro?.data?.emailSender.email}`} className="reschedule-mail-button">
                                <FontAwesomeIcon icon={faEnvelope} className="reschedule-mail-recruiter-icon" />
                                {meetIntro?.data?.emailSender.email}
                            </a>
                        </div>
                    </div>
                </div>
                <div className="flexCardBodyRight">
                    <div className="flexCardBodyRightHeader">
                        <span className="flexCardBodyRightTitle">{translate('reschedule:rescheduleInterview')}</span>
                        <span className="flexCardBodyRightDescription">
                            {translate('reschedule:whyDoYouNeedToReschedule')}
                        </span>
                    </div>
                    <div className="flexCardBodyRightList">
                        <Radio.Group className="flexCardBodyRightRadioGroup" value={rescheduleOpt.reasonCode}>
                            {reasons.map((value) => (
                                <Radio
                                    className="flexCardBodyRightRadioOption"
                                    key={value}
                                    value={value}
                                    onChange={(e) =>
                                        setRescheduleOpt({
                                            ...rescheduleOpt,
                                            reasonCode: e.target.value,
                                        })
                                    }
                                >
                                    {translate(`rescheduleOptions:${value}`)}
                                </Radio>
                            ))}
                        </Radio.Group>

                        {isOtherOptionSelected(rescheduleOpt.reasonCode) ? (
                            <div className="flexCardBodyRightReasonContainer">
                                <input
                                    className="flexCardBodyRightReasonInput"
                                    placeholder={translate('reschedule:customReason')}
                                    onChange={(event) =>
                                        setRescheduleOpt({
                                            ...rescheduleOpt,
                                            note: event?.target?.value,
                                        })
                                    }
                                    value={rescheduleOpt.note}
                                ></input>
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className="flexCardFooter">
                    <Button
                        onClick={onReschedule}
                        className={`rescheduleBtn ${isDisabled ? 'rescheduleBtnDisabled' : ''}`}
                        style={isDisabled ? {} : dynamicButtonStyle}
                        disabled={isDisabled}
                    >
                        {translate('reschedule:continue')}
                    </Button>
                </div>
            </div>
        </Wrapper>
    );
};

export default Reschedule;
